import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { HiArrowRight } from "react-icons/hi";
import Navbar from "./Components/Navbar";
import Footer from "./Components/Footer";

import { useLanguage } from "../../contexts/LanguageContext";
import { useTranslateItems } from "../../hooks/useTranslate";

// ─── Teks UI statis ────────────────────────────────────────────────────────────
const UI_TEXT = {
  EN: {
    heroLabel:    "Portfolio",
    heroBody:     "We design programs that drive innovation and accelerate meaningful change for impactful businesses.",
    heroBody2:    "Explore our portfolio and see how we empower businesses to expand their impact.",
    eyebrow:      "Our Portfolio",
    title:        "Programs & Projects",
    subtitle:     "Each project reflects our commitment to food security and community empowerment in Aceh.",
    learnMore:    "Learn More",
    ctaTitle:     "Want to collaborate with us?",
    ctaSub:       "We are always open to partnerships that impact food security in Aceh.",
    ctaBtn:       "Contact Us",
  },
  ID: {
    heroLabel:    "Portfolio",
    heroBody:     "Kami merancang program yang mendorong inovasi dan mempercepat perubahan bermakna bagi bisnis berdampak.",
    heroBody2:    "Jelajahi portofolio kami dan lihat bagaimana kami memberdayakan bisnis untuk memperluas dampaknya.",
    eyebrow:      "Portofolio Kami",
    title:        "Program & Proyek",
    subtitle:     "Setiap proyek mencerminkan komitmen kami terhadap ketahanan pangan dan pemberdayaan komunitas di Aceh.",
    learnMore:    "Pelajari Lebih",
    ctaTitle:     "Ingin berkolaborasi bersama kami?",
    ctaSub:       "Kami selalu terbuka untuk kemitraan yang berdampak bagi ketahanan pangan Aceh.",
    ctaBtn:       "Hubungi Kami",
  },
};

// ─── useInView ─────────────────────────────────────────────────────────────────
const useInView = (ref, threshold = 0.1) => {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return inView;
};

// ─── Hero ──────────────────────────────────────────────────────────────────────
const Hero = () => {
  const { language } = useLanguage();
  const tx = UI_TEXT[language];

  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 80);
    return () => clearTimeout(t);
  }, []);

  const heroImages = [
    "https://i.ibb.co.com/VW6N2JDc/IMG-4567.jpg",
    "https://i.ibb.co.com/DPTHdVs2/gambar.jpg",
    "https://i.ibb.co.com/bRMTtTXd/Nutrition-education-and-food-donation-for-breastfeeding-and-pregnant-women.jpg",
    "https://i.ibb.co.com/zhN2KP84/IMG-0069.jpg",
    "https://i.ibb.co.com/ccxMsrBs/Vegetable-donation-buy-or-donate-program-to-the-Limpok-Village.jpg",
    "https://i.ibb.co.com/jPsSsKDZ/Donasi-sayur.jpg",
    ];

  return (
    <section className="relative w-full overflow-hidden">
      <div
        className="absolute left-0 top-0 h-full w-1 bg-[#c9862a] z-20"
        style={{
          transform: loaded ? "scaleY(1)" : "scaleY(0)",
          transformOrigin: "top",
          transition: "transform 1.1s cubic-bezier(0.16,1,0.3,1) 0.2s",
        }}
      />

      <div className="grid grid-cols-3 grid-rows-2 h-[300px] md:h-[360px]">
        {heroImages.map((src, i) => (
          <div key={i} className="overflow-hidden">
            <img
              src={src} alt=""
              className="w-full h-full object-cover brightness-[0.55]"
              style={{
                transition: "transform 8s ease",
                transform: loaded ? "scale(1)" : "scale(1.04)",
              }}
            />
          </div>
        ))}
      </div>

      <div className="absolute inset-0 bg-gradient-to-r from-[#0d0c0a]/75 via-[#1a1a18]/50 to-[#0d0c0a]/30" />

      <div className="absolute inset-0 flex items-center px-8 md:px-16 lg:px-24">
        <div
          className="max-w-4xl"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? "translateY(0)" : "translateY(24px)",
            transition: "all 1s ease 0.4s",
          }}
        >
          <div className="flex items-center gap-3 mb-5">
            <div className="h-px w-10 bg-[#c9862a]" />
            <span className="text-[#c9862a] text-xs font-bold tracking-[4px] uppercase">
              {tx.heroLabel}
            </span>
          </div>

          {/* Teks hero dari UI_TEXT — langsung ganti saat bahasa berubah */}
          <p className="text-white text-sm md:text-2xl lg:text-3xl leading-[1.55] font-medium">
            {tx.heroBody}
          </p>
          <p className="text-white/80 text-sm md:text-xl leading-[1.6] mt-5 font-normal">
            {tx.heroBody2}
          </p>
        </div>
      </div>
    </section>
  );
};

// ─── Program Card ──────────────────────────────────────────────────────────────
const ProgramCard = ({ program, idx, inView, learnMore }) => {
  const navigate = useNavigate();
  const hasImage = program.url && program.url !== null;
  
  const toSlug = (text) =>
  text
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // hilangkan diakritik
    .replace(/[^a-z0-9\s-]/g, "")  // hapus karakter selain huruf/angka/spasi/dash
    .trim()
    .replace(/\s+/g, "-");
 
  return (
    <div
      className="bg-white rounded-2xl shadow-sm border border-[#e8e0d0] flex flex-col overflow-hidden group hover:shadow-lg hover:border-[#c9862a]/30 transition-all duration-300"
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(32px)",
        transition: `all 0.65s ease ${0.06 * idx}s`,
      }}
    >
      {/* ── Gambar: hanya tampil jika ada URL ── */}
      {hasImage && (
        <div className="h-52 flex items-center justify-center p-10 bg-[#faf6ef] relative overflow-hidden border-b border-[#e8e0d0]">
          <img
            src={program.url}
            alt={program.judul}
            className="max-h-24 max-w-[180px] object-contain group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute bottom-0 left-0 h-[3px] w-0 bg-[#c9862a] group-hover:w-full transition-all duration-500" />
        </div>
      )}
 
      {/* ── Konten ── */}
      <div className="p-7 flex flex-col flex-1">
        {/* Judul */}
        <h3 className="font-serif text-[17px] font-bold text-[#1a1a18] leading-snug mb-4">
          {program.judul}
        </h3>
 
        <div className="w-6 h-[2px] bg-[#c9862a]/40 mb-4 group-hover:w-12 transition-all duration-300" />
 
        {/* Deskripsi — dibatasi 3 baris dengan line-clamp */}
        <p className="text-[#6b6456] text-sm leading-relaxed flex-1 line-clamp-3">
          {program.deskripsi}
        </p>
 
        {/* Tombol */}
        <div className="mt-7 pt-6 border-t border-[#f0ece3]">
          <button
            onClick={() => navigate(`/view-program/${toSlug(program.judul)}`)}
            className="flex items-center gap-2 border border-[#c9862a] text-[#1a1a18] text-sm font-semibold px-6 py-2.5 rounded-full hover:bg-[#c9862a] hover:text-white transition-all duration-200 group/btn"
          >
            {learnMore}
            <HiArrowRight size={13} className="transition-transform duration-300 group-hover/btn:translate-x-1" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Portfolio Grid ────────────────────────────────────────────────────────────
const Program = () => {
  const { language } = useLanguage();
  const tx = UI_TEXT[language];

  const ref = useRef(null);
  const inView = useInView(ref);
  const [rawPrograms, setRawPrograms] = useState([]);

  const toSlug = (text) =>
  text
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // hilangkan diakritik
    .replace(/[^a-z0-9\s-]/g, "")  // hapus karakter selain huruf/angka/spasi/dash
    .trim()
    .replace(/\s+/g, "-");

  // 1. Fetch data mentah dari API (hanya sekali)
  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const apiUrl = process.env.REACT_APP_URL_API;
        const res = await axios.get(`${apiUrl}/all-program`);
        setRawPrograms(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Error fetching programs:", err);
      }
    };
    fetchPrograms();
  }, []);

  // 2. Terjemahkan field "judul" dan "deskripsi" secara otomatis via MyMemory
  //    Hanya aktif saat language === "EN", hasil di-cache per sesi
  const { translated: programs, loading } = useTranslateItems(
    rawPrograms,
    language,
    ["deskripsi"]  // ← field dari API yang ingin diterjemahkan
  );

  return (
    <section ref={ref} className="bg-[#f7f3ec] py-20 px-4 md:px-8">
      <div className="container max-w-7xl mx-auto">

        {/* Header */}
        <div
          className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6"
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(24px)",
            transition: "all 0.8s ease",
          }}
        >
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-10 bg-[#c9862a]" />
              <span className="text-[#c9862a] text-xs font-bold tracking-[4px] uppercase">
                {tx.eyebrow}
              </span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#1a1a18] leading-tight">
              {tx.title}
            </h2>
          </div>
          <p className="text-[#6b6456] text-base leading-relaxed max-w-sm md:text-right">
            {tx.subtitle}
          </p>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-4 mb-14">
          <div className="flex-1 h-px bg-[#e8e0d0]" />
          <div className="w-2 h-2 bg-[#c9862a] rotate-45" />
          <div className="flex-1 h-px bg-[#e8e0d0]" />
        </div>

        {/* Loading skeleton saat menerjemahkan */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {rawPrograms.map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-[#e8e0d0] overflow-hidden animate-pulse">
                <div className="h-52 bg-[#f0ece3]" />
                <div className="p-7 flex flex-col gap-3">
                  <div className="h-4 bg-[#e8e0d0] rounded w-3/4" />
                  <div className="h-3 bg-[#e8e0d0] rounded w-full" />
                  <div className="h-3 bg-[#e8e0d0] rounded w-5/6" />
                  <div className="h-3 bg-[#e8e0d0] rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {programs.map((program, idx) => (
              <ProgramCard
                key={program.id}
                program={program}
                idx={idx}
                inView={inView}
                learnMore={tx.learnMore}
              />
            ))}
          </div>
        )}

        {/* CTA */}
        <div
          className="mt-16 border-t border-[#e8e0d0] pt-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
          style={{
            opacity: inView ? 1 : 0,
            transition: "opacity 0.8s ease 0.6s",
          }}
        >
          <div>
            <p className="font-serif text-2xl font-bold text-[#1a1a18] mb-1">{tx.ctaTitle}</p>
            <p className="text-[#a09880] text-sm">{tx.ctaSub}</p>
          </div>
          <button
            onClick={() =>
              window.open(
                "https://api.whatsapp.com/send/?phone=6285385482041&text&type=phone_number&app_absent=0",
                "_blank",
                "noopener,noreferrer"
              )
            }
          className="flex items-center gap-3 bg-[#1a1a18] hover:bg-[#c9862a] text-white text-sm font-bold tracking-widest uppercase px-8 py-4 transition-all duration-300 whitespace-nowrap group/btn">
            {tx.ctaBtn}
            <HiArrowRight className="transition-transform duration-300 group-hover/btn:translate-x-1" />
          </button>
        </div>
      </div>
    </section>
  );
};

// ─── Page ──────────────────────────────────────────────────────────────────────
const PortfolioPage = () => (
  <main className="overflow-x-hidden font-homepage bg-white">
    <Navbar />
    <section id="hero"><Hero /></section>
    <section id="program"><Program /></section>
    <section id="footer"><Footer /></section>
  </main>
);

export default PortfolioPage;